import React, { createContext, useContext, useEffect, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from './AuthContext';

interface SocketContextType {
  socket: Socket | null;
  isConnected: boolean;
}

const SocketContext = createContext<SocketContextType>({
  socket: null,
  isConnected: false,
});

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

interface SocketProviderProps {
  children: React.ReactNode;
}

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const { getAccessToken, isAuthenticated } = useAuth();

  useEffect(() => {
    if (!isAuthenticated) {
      // If not authenticated, don't establish socket connection
      return;
    }

    const initializeSocket = async () => {
      try {
        // Get the access token for authentication
        const token = await getAccessToken();
        
        // Create socket connection with authentication
        const socketIO = io(
          import.meta.env.VITE_WEBSOCKET_URL
            || import.meta.env.VITE_API_BASE_URL
            || (import.meta.env.DEV ? 'http://localhost:8080' : window.location.origin),
          {
            transports: ['websocket', 'polling'],
            auth: {
              token: token || undefined,
            },
          }
        );

        socketIO.on('connect', () => {
          console.log('Connected to server');
          setIsConnected(true);
        });

        socketIO.on('disconnect', () => {
          console.log('Disconnected from server');
          setIsConnected(false);
        });

        socketIO.on('connect_error', (error) => {
          console.error('Connection error:', error);
          setIsConnected(false);
        });

        setSocket(socketIO);

        return () => {
          socketIO.close();
        };
      } catch (error) {
        console.error('Failed to initialize socket with authentication:', error);
      }
    };

    const cleanup = initializeSocket();
    
    return () => {
      if (cleanup instanceof Promise) {
        cleanup.then(cleanupFn => cleanupFn && cleanupFn());
      }
    };
  }, [isAuthenticated, getAccessToken]);

  const value: SocketContextType = {
    socket,
    isConnected,
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};